import * as React from "react"

import { useUserQuery } from "shared/hooks/queries"
import { Config, ServerConfig } from "shared/utils/config"

interface ConfigContextInterface {
  config: Config
}

const ConfigContext = React.createContext<ConfigContextInterface & { initialised: boolean }>({
  ...({} as ConfigContextInterface),
  initialised: false,
})

export const ConfigContextProvider = ({
  serverConfig,
  appInstanceId,
  children,
}: {
  serverConfig: ServerConfig
  appInstanceId: string
  children: React.ReactNode
}): JSX.Element | null => {
  const { data: user } = useUserQuery(appInstanceId)

  const config = React.useMemo<Config>(() => {
    return {
      ...serverConfig,
      appInstanceId,
      // This object is thrown away if user is undefined, so we don't need to worry about nullability.
      user: user!,
    }
  }, [serverConfig, appInstanceId, user])

  if (!user) return null

  return <ConfigContext.Provider value={{ config, initialised: true }}>{children}</ConfigContext.Provider>
}

export const useConfig = (): Config => {
  const context = React.useContext(ConfigContext)
  if (!context.initialised) {
    throw new Error("useConfig must be used within an ConfigContextProvider")
  }
  return context.config
}
